import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EventMaster from '../components/EventMaster';
import GalleryManager from '../components/GalleryManager';
import MemberManager from '../components/MemberManager';

// import UpcomingEvents from '../components/UpcomingEvents';
import API_CONFIG, { API_URLS } from '../config/api';
import { getAuthToken, createAuthHeaders, withLoading, handleApiError } from '../utils/apiHelpers';
import { FaTrash } from 'react-icons/fa';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('players');
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchMethod, setSearchMethod] = useState('all'); // Track search method
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState('');
  const [showRemarksModal, setShowRemarksModal] = useState(false);

  // Fetch search suggestions for real-time search
  const fetchSearchSuggestions = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(`/api/playerNew/search?q=${encodeURIComponent(term.trim())}`, {
        headers: createAuthHeaders(),
        timeout: API_CONFIG.TIMEOUT
      });

      if (response.data && response.data.success) {
        const suggestions = response.data.players.slice(0, 8); // Limit to 8 suggestions
        setSearchSuggestions(suggestions);
        setShowSuggestions(true);
        setSelectedSuggestionIndex(-1);
      }
    } catch (error) {
      // console.error('Search suggestions error:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const fetchPlayers = useCallback(async () => {
    try {
      // Check token before API call
      const token = localStorage.getItem('adminToken');
      // console.log('🔑 Admin Token:', token ? 'Present' : 'Missing');
      
      await withLoading(setLoading, async () => {
        let response;
        
        // Use unified search endpoint for all searches
        if (searchTerm.trim()) {
          setSearchMethod('unified');
          response = await axios.get(`/api/playerNew/search?q=${encodeURIComponent(searchTerm.trim())}`, {
            headers: createAuthHeaders(),
            timeout: API_CONFIG.TIMEOUT
          });
        } else {
          // No search term - get all players
          setSearchMethod('all');
          // console.log('🌐 API URL:', API_URLS.PLAYER_LIST);
          // console.log('🔑 Headers:', createAuthHeaders());
          response = await axios.get(API_URLS.PLAYER_NEW_LIST, {
            headers: createAuthHeaders(),
            timeout: API_CONFIG.TIMEOUT
          });
        }
      
      // Debug logs
      // console.log('🔍 API Response:', response.data);
      // console.log('🔍 Response Status:', response.status);
      // console.log('🔍 Players Array:', response.data.players);
      // console.log('🔍 Players Length:', response.data.players?.length);
      // console.log('🔍 Response Success:', response.data.success);
      
      // Handle the nested players array from server response
      if (response.data && response.data.success && Array.isArray(response.data.players)) {
        // console.log('✅ Players data found:', response.data.players.length, 'players');
        // Filter out soft deleted players (isActive = 0)
        const activePlayers = response.data.players.filter(player => player.isActive !== 0);
        // console.log('✅ Active players after filter:', activePlayers.length, 'players');
        setPlayers(activePlayers);
      } else if (response.data && response.data.success === false) {
        // console.log('❌ API Error:', response.data.error);
        setError(response.data.error || 'Failed to fetch players');
        setPlayers([]);
      } else {
        // console.log('⚠️ No players array found in response');
        // console.log('⚠️ Response structure:', Object.keys(response.data || {}));
        setPlayers([]);
      }
      });
    } catch (error) {
      // console.log('❌ API Call Error:', error);
      // console.log('❌ Error Response:', error.response?.data);
      // console.log('❌ Error Status:', error.response?.status);
      handleApiError(error, setError);
      setPlayers([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    
    // Debounce search to prevent excessive requests
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        // Fetch suggestions for real-time search
        fetchSearchSuggestions(searchTerm);
        // Also fetch main results
        fetchPlayers();
      } else {
        // Clear suggestions and fetch all players
        setSearchSuggestions([]);
        setShowSuggestions(false);
        fetchPlayers();
      }
    }, 300); // Reduced to 300ms for better responsiveness
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchTerm, fetchSearchSuggestions]);

  // Handle Escape key to close image modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showImageModal) {
        setShowImageModal(false);
      }
    };
    
    if (showImageModal) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [showImageModal]);

  const exportToCSV = async () => {
    try {
      await withLoading(setLoading, async () => {
        const response = await axios.get(API_URLS.PLAYER_NEW_EXPORT, {
          headers: createAuthHeaders(),
          responseType: 'blob',
          timeout: API_CONFIG.TIMEOUT
        });
      
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `registered_players_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
    } catch (error) {
      handleApiError(error, setError);
    }
  };

  const deletePlayer = async (id) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      try {
        await axios.delete(`/api/playerNew/${id}`, {
          headers: createAuthHeaders(),
          timeout: API_CONFIG.TIMEOUT
        });
        fetchPlayers(); // Refresh the list
        alert('Player deleted successfully');
      } catch (error) {
        handleApiError(error, setError);
      }
    }
  };

  const openRemarksModal = (remarks) => {
    setSelectedRemarks(remarks || 'No remarks available');
    setShowRemarksModal(true);
  };

  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || searchSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < searchSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : searchSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < searchSuggestions.length) {
          selectSuggestion(searchSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Handle suggestion selection
  const selectSuggestion = (player) => {
    setSearchTerm(player.name || player.phone || player.receipt_number || '');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    
    // Add to search history
    const searchItem = player.name || player.phone || player.receipt_number || '';
    if (searchItem && !searchHistory.includes(searchItem)) {
      setSearchHistory(prev => [searchItem, ...prev.slice(0, 9)]); // Keep last 10 searches
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(value.trim().length >= 2);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <button
            onClick={() => {
              localStorage.removeItem('adminToken');
              localStorage.removeItem('tokenExpiry');
              localStorage.removeItem('rememberMe');
              window.dispatchEvent(new Event('admin-logout'));
              navigate('/admin');
            }}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
          >
            Logout
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('players')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'players'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Players
            </button>
            <button
              onClick={() => setActiveTab('eventMaster')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'eventMaster'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Event Master
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gallery'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Gallery Management
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'members'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Member Management
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'players' && (
          <div>
            {/* Search and Export Section */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="px-8 py-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Player Management</h3>
              </div>
              <div className="px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="relative flex-1 max-w-md overflow-visible">
                  <input
                    type="text"
                    placeholder="Search by name, mobile, or receipt number"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowSuggestions(searchTerm.trim().length >= 2)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <svg
                    className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {searchTerm && (
                    <div className="absolute right-3 top-3.5">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        searchMethod === 'unified' ? 'bg-purple-100 text-purple-800' :
                        searchMethod === 'all' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {searchMethod === 'unified' ? '🔍' : 
                         searchMethod === 'all' ? '📋' : '🔍'}
                      </span>
                    </div>
                  )}
                  
                  {/* Search Suggestions Dropdown */}
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto top-full left-0">
                      {searchSuggestions.map((player, index) => (
                        <div
                          key={player.id}
                          className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                            index === selectedSuggestionIndex ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => selectSuggestion(player)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">
                                {player.name || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                <span className="mr-3">📱 {player.phone || 'N/A'}</span>
                                <span>🧾 {player.receipt_number || 'N/A'}</span>
                              </div>
                            </div>
                            <div className="text-xs text-gray-400">
                              {player.position || 'N/A'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Search History */}
                  {showSuggestions && searchSuggestions.length === 0 && searchHistory.length > 0 && searchTerm.trim().length >= 2 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl top-full left-0">
                      <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                        Recent Searches
                      </div>
                      {searchHistory.slice(0, 5).map((item, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-50 text-sm"
                          onClick={() => {
                            setSearchTerm(item);
                            setShowSuggestions(false);
                          }}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={exportToCSV}
                  className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors duration-200 font-medium flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export to CSV
                </button>
              </div>
            </div>

            {/* Players Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Registered Players</h3>
              </div>
              <div className="w-full overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[4%]">
                        S.No
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                        Name
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[5%]">
                        Age
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">
                        Mobile
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                        Address
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">
                        Playing Style
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[7%]">
                        Player Type
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[7%]">
                        Reg. Date
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">
                        Receipt No
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[6%]">
                        Amount
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[7%]">
                        Unavailability
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[7%]">
                        Remarks
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[5%]">
                        Photo
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[5%]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="14" className="px-1 py-6 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-500">Loading players...</span>
                          </div>
                        </td>
                      </tr>
                    ) : players && Array.isArray(players) && players.length > 0 ? (
                      players.map((player, index) => (
                        <tr key={player._id} className="hover:bg-gray-50">
                          <td className="px-2 py-3 text-center text-gray-500 text-xs font-medium">
                            {index + 1}
                          </td>
                          <td className="px-2 py-3 font-medium text-gray-900 text-xs">{player.name || player._id || 'N/A'}</td>
                          <td className="px-2 py-3 text-center text-gray-500 text-xs">{player.age}</td>
                          <td className="px-2 py-3 text-gray-500 text-xs">{player.phone || 'N/A'}</td>
                          <td 
                            className="px-2 py-3 text-gray-500 text-xs max-w-[120px] truncate cursor-help" 
                            title={player.address ? player.address.trim() : 'N/A'}
                          >
                            {player.address ? player.address.trim().substring(0, 30) + (player.address.trim().length > 30 ? '...' : '') : 'N/A'}
                          </td>
                          <td className="px-2 py-3 text-gray-500 text-xs">{player.playingStyle || 'N/A'}</td>
                          <td className="px-2 py-3 text-gray-500 text-xs">{player.position || 'N/A'}</td>
                          <td className="px-2 py-3 text-center text-gray-500 text-xs">
                            {player.createdAt || player.created_at
                              ? new Date(player.createdAt || player.created_at).toLocaleDateString('en-GB')
                              : 'N/A'}
                          </td>
                          <td className="px-2 py-3 text-center text-gray-500 text-xs font-mono">
                            {player.receipt_number ? (
                              <button
                                onClick={() => {
                                  const token = localStorage.getItem('adminToken');
                                  const url = token
                                    ? `/api/playerNew/receipt/${player.id || player._id}?token=${encodeURIComponent(token)}`
                                    : `/api/playerNew/receipt/${player.id || player._id}`;
                                  window.open(url, '_blank');
                                }}
                                className="text-blue-600 hover:underline"
                                title="View receipt"
                              >
                                {player.receipt_number}
                              </button>
                            ) : 'N/A'}
                          </td>
                          <td className="px-2 py-3 text-center text-gray-500 text-xs">
                            {(() => {
                              let displayAmount = player.amount || player.registrationFee;
                              if (displayAmount) {
                                return `₹${displayAmount}`;
                              }
                              // Fallback based on player position
                              if (player.position === 'New Player') return '₹1500';
                              if (player.position === 'Old Player') return '₹1000';
                              if (player.position === 'Child Player (Kabir Nagar)') return '₹700';
                              return 'N/A';
                            })()}
                          </td>
                          <td className="px-2 py-3 text-center text-gray-500 text-xs">
                            {player.unavailability ? new Date(player.unavailability).toLocaleDateString('en-GB') : 'Available'}
                          </td>
                          <td className="px-2 py-3 text-gray-500 text-xs">
                            <div 
                              className="truncate max-w-[80px] cursor-pointer hover:text-blue-600 transition-colors duration-200"
                              onClick={() => openRemarksModal(player.remarks)}
                              title={player.remarks || 'No remarks'}
                            >
                              {player.remarks ? (player.remarks.length > 15 ? player.remarks.substring(0, 15) + '...' : player.remarks) : 'No remarks'}
                            </div>
                          </td>
                          <td className="px-2 py-3 text-center">
                            {player.photo ? (
                              <img
                                src={player.photo.startsWith('/uploads/') 
                                  ? player.photo
                                  : `/uploads/${player.photo}`
                                }
                                alt="Player"
                                className="w-12 h-12 rounded-full object-cover cursor-pointer hover:scale-125 transition-transform duration-200 shadow-md border-2 border-gray-200"
                                onClick={() => {
                                  const imageUrl = player.photo.startsWith('/uploads/') 
                                    ? player.photo
                                    : `/uploads/${player.photo}`;
                                  setSelectedImage(imageUrl);
                                  setShowImageModal(true);
                                }}
                                onError={(e) => {
                                  // console.log('❌ Image load error for:', player.photo);
                                  // console.log('❌ Attempted URL:', e.target.src);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                                onLoad={() => {
                                  // console.log('✅ Image loaded successfully:', player.photo);
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center shadow-md border-2 border-gray-200">
                                <span className="text-gray-400 text-lg">👤</span>
                              </div>
                            )}
                            <div 
                              style={{display: 'none'}} 
                              className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center shadow-md border-2 border-gray-200"
                            >
                              <span className="text-gray-400 text-lg">👤</span>
                            </div>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <div
                              onClick={() => deletePlayer(player.id)}
                              className="bg-yellow-100 hover:bg-yellow-200 text-orange-600 w-7 h-7 rounded-md transition-colors duration-200 flex items-center justify-center cursor-pointer mx-auto"
                              title="Delete player"
                            >
                              <FaTrash className="w-3.5 h-3.5" />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="14" className="px-1 py-6 text-center">
                          <div className="flex flex-col items-center text-gray-500">
                            <svg className="w-12 h-12 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <p className="text-sm">
                              {searchTerm ? 'No players found matching your search.' : 'No players registered yet.'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'eventMaster' && <EventMaster />}
        
        {activeTab === 'gallery' && <GalleryManager />}
        
        {activeTab === 'members' && <MemberManager />}
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(false);
              }}
              className="absolute -top-12 right-0 bg-white hover:bg-gray-200 text-gray-800 rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold shadow-lg transition-colors duration-200 z-10"
              title="Close (Esc)"
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Player Photo"
              className="w-full h-auto rounded-lg shadow-2xl border-4 border-white max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Remarks Modal */}
      {showRemarksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative max-w-md mx-4 bg-white rounded-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Player Remarks</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                {selectedRemarks}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowRemarksModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
